import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'

import { Box, Text, theme } from '@factorialco/deprecated-design-system'
import { F0Button, F0TagRaw, UpsellRequestResponseDialog } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { ChevronLeft, ChevronRight, Cross } from '@factorialco/f0-react/icons/app'
import Share from '@factorialco/f0-react/icons/app/Share'
import Upsell from '@factorialco/f0-react/icons/app/Upsell'
import { Features } from 'generated/featureDefinitions'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useMarketplaceModulesPath } from 'modules/marketplace_modules/hooks/useMarketplaceModulesPath'
import { UpsellGuard } from 'modules/upselling_kit/components/UpsellGuard'
import { useGetUpsellingData } from 'modules/upselling_kit/hooks/useGetUpsellingData'
import { UpsellingKitModule } from 'modules/upselling_kit/types'
import { useDefaultMessages } from 'modules/upselling_kit/utils/defaultMessages'
import {
  buildUpsellOriginParam,
  handleUpsellingLearnMore,
  handleUpsellingOnClose,
  handleUpsellingVisibility,
} from 'modules/upselling_kit/utils/handlers'

interface FeatureModalProps extends Omit<UpsellingKitModule, 'featureRequested'> {
  featureRequested: Features // Mandatory for FeatureModal
  isOpen: boolean
  onClose: () => void
  openFromUpsellButton?: boolean
}

const FeatureModalContent = ({
  moduleRequested,
  featureRequested,
  isOpen,
  onClose,
  requestSource,
  openFromUpsellButton,
}: FeatureModalProps) => {
  const navigate = useNavigate()
  const { getDetailPath } = useMarketplaceModulesPath()
  const [upsellSuccess, setUpsellSuccess] = useState(false)
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0)
  const {
    moduleName,
    upsellModalDescription,
    upsellModalImage: defaultUpsellModalImage,
    moduleSlug,
    loading,
    featureModals,
  } = useGetUpsellingData({
    moduleRequested,
    featureRequested,
  })

  // Filter featureModals to only show ones matching the slug and flatten featureSlide arrays
  const filteredFeatureSlides = useMemo(() => {
    if (!featureRequested || !featureModals) return []
    const matchingModals = featureModals.filter((modal) => {
      return modal?.featureSlug === featureRequested
    })

    // Flatten all featureSlide arrays from matching modals
    return matchingModals.flatMap((modal) => modal.featureSlide || [])
  }, [featureRequested, featureModals])

  // Initialize current index when filtered feature slides are loaded
  useEffect(() => {
    if (filteredFeatureSlides.length > 0) {
      setCurrentFeatureIndex(0)
    }
  }, [filteredFeatureSlides])

  // Get current feature slide to display
  const currentFeatureSlide =
    filteredFeatureSlides.length > 0 ? filteredFeatureSlides[currentFeatureIndex] : null

  const totalFeatures = filteredFeatureSlides.length

  const goToFeature = (index: number) => {
    if (index >= 0 && index < totalFeatures) {
      setCurrentFeatureIndex(index)
    }
  }

  const goToPrevious = () => goToFeature(currentFeatureIndex - 1)
  const goToNext = () => goToFeature(currentFeatureIndex + 1)

  const defaultMessages = useDefaultMessages({
    component: 'feature_modal',
    requestSource,
  })
  const { company } = useCurrentSession()

  useEffect(() => {
    if (isOpen && !openFromUpsellButton) {
      handleUpsellingVisibility({
        moduleRequested,
        requestSource,
        component: 'feature_modal',
      })
    } else if (isOpen && openFromUpsellButton) {
      track(trackingEvents.upsellingKit.openProductModal, {
        source: requestSource,
        module: moduleRequested,
        component: 'feature_modal',
      })
    }
  }, [isOpen, moduleRequested, requestSource, openFromUpsellButton])

  if (loading) {
    return (
      <Box
        width="full"
        height="full"
        justifyContent="center"
        alignItems="center"
        style={{
          background: 'rgba(0,0,0,0.3)',
          zIndex: theme.zIndex.modals,
          position: 'fixed',
        }}
      >
        <Spinner />
      </Box>
    )
  }

  const handleOnClose = () => {
    handleUpsellingOnClose({
      onClose,
      requestSource,
      moduleRequested,
      component: 'feature_modal',
    })
  }

  const handleLearnMore = () => {
    handleUpsellingLearnMore({
      moduleRequested,
      requestSource,
      component: 'feature_modal',
    })

    const upsellOriginParam = buildUpsellOriginParam(
      'feature_modal',
      requestSource,
      'Upselling Kit'
    )
    const path = getDetailPath(moduleSlug || '', upsellOriginParam)

    if (company.id === 65466) {
      window.open(`${window.location.origin}${path}`, '_blank')
    } else {
      navigate(path)
    }
    onClose()
  }

  const handleUpsellClose = () => {
    setUpsellSuccess(false)
    onClose()
  }

  // Use current feature slide data if available, otherwise fall back to defaults
  const featureSlideImage = currentFeatureSlide?.image?.url
    ? `${currentFeatureSlide.image.url}`
    : null
  const featureSlideTitle = currentFeatureSlide?.title
  const featureSlideDescription = currentFeatureSlide?.description

  const displayImage = featureSlideImage || defaultUpsellModalImage || ''
  const displayTitle = featureSlideDescription || upsellModalDescription || ''
  const displayModalTitle = featureSlideTitle || moduleName || ''

  if (!isOpen) return null

  const modalContent = (
    <>
      <Box
        width="full"
        height="full"
        justifyContent="center"
        alignItems="center"
        style={{
          background: 'rgba(0,0,0,0.3)',
          zIndex: theme.zIndex.modals,
          position: 'fixed',
          top: '0px',
          left: '0px',
          right: '0px',
          bottom: '0px',
        }}
        onClick={handleOnClose}
      >
        <Box
          background="grey000White"
          borderRadius={{ all: 'abs016' }}
          position="relative"
          style={{
            overflow: 'auto',
            maxHeight: '90vh',
            width: '840px',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box
            position="absolute"
            style={{
              top: '8px',
              right: '8px',
              zIndex: theme.zIndex.modals + 2,
            }}
          >
            <F0Button
              onClick={handleOnClose}
              variant="outline"
              size="md"
              icon={Cross}
              hideLabel
              round
              label="Close"
            />
          </Box>

          {displayImage && (
            <Box padding="s4">
              <Box
                width="full"
                borderRadius={{ all: 'abs012' }}
                style={{
                  height: '472px',
                  backgroundImage: `url(${displayImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
            </Box>
          )}

          <Box padding="s16" paddingBottom="s24" gap="s24" alignItems="flexStart">
            <Box gap="s8" alignItems="flexStart" style={{ width: '100%' }}>
              <Box paddingY="s8">
                <F0TagRaw icon={Upsell} text={i18n.t('upselling_kit.upgrade')} />
              </Box>
              <Text.Header2 weight="semibold" ellipsis lineClamp={2}>
                {displayModalTitle}
              </Text.Header2>
              <Text.Normal size="300" color="grey700" ellipsis lineClamp={3}>
                {displayTitle}
              </Text.Normal>
            </Box>
            <Box
              flexDirection="row"
              justifyContent="spaceBetween"
              gap="s16"
              paddingY="s4"
              alignItems="center"
              style={{ width: '100%' }}
            >
              <F0Button
                label={i18n.t('upselling_kit.button.learn_more')}
                onClick={handleLearnMore}
                variant="default"
                size="md"
                icon={Share}
                style={{ flex: 1 }}
              />

              {totalFeatures > 1 && (
                <Box flexDirection="row" gap="s8" alignItems="center">
                  <Box style={{ width: '24px', height: '24px' }}>
                    <F0Button
                      onClick={goToPrevious}
                      variant="outline"
                      size="sm"
                      icon={ChevronLeft}
                      hideLabel
                      round
                      label="Previous"
                      disabled={currentFeatureIndex === 0}
                    />
                  </Box>

                  <Box flexDirection="row" gap="s4" alignItems="center">
                    {filteredFeatureSlides.map((_, index) => {
                      const isActive = index === currentFeatureIndex
                      return (
                        <Box
                          key={index}
                          onClick={() => goToFeature(index)}
                          style={{
                            width: isActive ? '24px' : '8px',
                            height: '8px',
                            borderRadius: '4px',
                            backgroundColor: isActive
                              ? theme.colors.grey1000
                              : theme.colors.grey300,
                            cursor: 'pointer',
                            transition:
                              'background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        />
                      )
                    })}
                  </Box>

                  <Box style={{ width: '24px', height: '24px' }}>
                    <F0Button
                      onClick={goToNext}
                      variant="outline"
                      size="sm"
                      icon={ChevronRight}
                      hideLabel
                      round
                      label="Next"
                      disabled={currentFeatureIndex === totalFeatures - 1}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )

  return (
    <>
      {typeof document !== 'undefined' && createPortal(modalContent, document.body)}
      <UpsellRequestResponseDialog
        open={upsellSuccess}
        success={upsellSuccess}
        portalContainer={document.getElementById('root')}
        errorMessage={defaultMessages.error}
        successMessage={defaultMessages.success}
        closeLabel={defaultMessages.close}
        onClose={handleUpsellClose}
      />
    </>
  )
}

const FeatureModal = ({
  fallbackComponent,
  isOpen,
  onClose,
  openFromUpsellButton = true,
  featureRequested,
  ...upsellProps
}: FeatureModalProps) => {
  return (
    <UpsellGuard
      {...upsellProps}
      featureRequested={featureRequested}
      fallbackComponent={fallbackComponent || null}
    >
      <FeatureModalContent
        {...upsellProps}
        featureRequested={featureRequested}
        isOpen={isOpen}
        onClose={onClose}
        openFromUpsellButton={openFromUpsellButton}
      />
    </UpsellGuard>
  )
}

export { FeatureModal }
